import { gridWidthInCells, staggerOffsetCells } from '@miyuki/ui';

/**
 * Geometría del lienzo: nada de esto toca el DOM ni el canvas.
 *
 * Al dibujar sobre `<canvas>` la detección de toques se hace por aritmética y
 * no por eventos de elementos. Tenerla aquí, en funciones puras, la vuelve
 * comprobable sin montar nada.
 *
 * El "zoom" se expresa como el lado de la mostacilla en px CSS, no como un
 * factor abstracto: así el tope de RF-30 se lee directamente en el código.
 */

/** Tope de acercamiento: una mostacilla de 64 px (RF-30). */
export const MAX_CELL_SIZE = 64;

/** Suelo absoluto: por debajo, la cuadrícula deja de distinguirse. */
export const MIN_CELL_SIZE = 2;

export interface GridLayout {
  readonly columns: number;
  readonly rowCount: number;
  /** Ancho reservado a la izquierda para los números de fila. */
  readonly gutterWidth: number;
  /**
   * Las filas impares van corridas media mostacilla. La convención vive en
   * `@miyuki/ui`, compartida con el canvas que dibuja: si cada uno tuviera la
   * suya, el dedo pintaría corrido respecto de lo que se ve.
   */
  readonly staggered: boolean;
}

export interface Viewport {
  readonly width: number;
  readonly height: number;
}

export interface CanvasTransform {
  readonly cellSize: number;
  readonly offsetX: number;
  readonly offsetY: number;
}

export interface CellPosition {
  readonly row: number;
  readonly column: number;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/** Tamaño de celda con el que el diseño completo cabe en el viewport. */
export function fitCellSize(layout: GridLayout, viewport: Viewport): number {
  // El ancho incluye el saliente: sin él, "ver todo" recorta media mostacilla
  // de la última columna de las filas corridas.
  const byWidth =
    (viewport.width - layout.gutterWidth) / gridWidthInCells(layout.columns, layout.staggered);
  const byHeight = viewport.height / layout.rowCount;

  return Math.max(MIN_CELL_SIZE, Math.min(byWidth, byHeight));
}

/**
 * Acota el zoom entre "se ve el diseño entero" y "la mostacilla mide 64 px".
 *
 * En un diseño pequeño el tamaño de ajuste supera los 64 px, así que el rango
 * se colapsa en el tope: no tiene sentido acercar más de lo que RF-30 permite
 * solo porque sobre pantalla.
 */
export function clampCellSize(cellSize: number, layout: GridLayout, viewport: Viewport): number {
  const lower = Math.min(fitCellSize(layout, viewport), MAX_CELL_SIZE);
  return clamp(cellSize, lower, MAX_CELL_SIZE);
}

/** Celda bajo un punto en coordenadas del lienzo, o `null` si no hay ninguna. */
export function hitTest(
  x: number,
  y: number,
  layout: GridLayout,
  transform: CanvasTransform,
): CellPosition | null {
  const gridY = y - transform.offsetY;
  if (gridY < 0) return null;

  // La fila se resuelve primero porque el desfase es horizontal: cuánto hay que
  // descontar de la `x` depende de en qué fila cayó el dedo.
  const row = Math.floor(gridY / transform.cellSize);
  if (row < 0 || row >= layout.rowCount) return null;

  const stagger = staggerOffsetCells(row, layout.staggered) * transform.cellSize;
  const gridX = x - layout.gutterWidth - transform.offsetX - stagger;
  if (gridX < 0) return null;

  const column = Math.floor(gridX / transform.cellSize);
  if (column < 0 || column >= layout.columns) return null;

  return { row, column };
}

/** Filas que hay que dibujar: las de fuera de pantalla no se tocan. */
export function visibleRowRange(
  layout: GridLayout,
  transform: CanvasTransform,
  viewport: Viewport,
): { readonly first: number; readonly last: number } {
  const first = Math.floor(-transform.offsetY / transform.cellSize);
  const last = Math.ceil((-transform.offsetY + viewport.height) / transform.cellSize);

  return {
    first: clamp(first, 0, layout.rowCount - 1),
    last: clamp(last, 0, layout.rowCount - 1),
  };
}

/**
 * Impide arrastrar el diseño fuera de la vista. Lo que cabe entero se queda
 * pegado al origen en vez de poder empujarse a un rincón.
 */
export function clampOffset(
  transform: CanvasTransform,
  layout: GridLayout,
  viewport: Viewport,
): CanvasTransform {
  const contentWidth = gridWidthInCells(layout.columns, layout.staggered) * transform.cellSize;
  const contentHeight = layout.rowCount * transform.cellSize;
  const availableWidth = viewport.width - layout.gutterWidth;

  const minOffsetX = Math.min(0, availableWidth - contentWidth);
  const minOffsetY = Math.min(0, viewport.height - contentHeight);

  return {
    ...transform,
    offsetX: clamp(transform.offsetX, minOffsetX, 0),
    offsetY: clamp(transform.offsetY, minOffsetY, 0),
  };
}

export interface CanvasPoint {
  readonly x: number;
  readonly y: number;
}

/**
 * Celdas que atraviesa el recorrido del dedo entre dos puntos del lienzo.
 *
 * Un `pointermove` puede saltar varias celdas de una vez: a velocidad de
 * dibujo, pintar solo la celda del evento deja huecos en el trazo. Se muestrea
 * el segmento a pasos de media celda —lo bastante fino para no saltarse
 * ninguna— y se descartan las repeticiones y lo que cae fuera de la grilla o
 * sobre el gutter, que `hitTest` ya resuelve devolviendo `null`.
 */
export function cellsBetween(
  from: CanvasPoint,
  to: CanvasPoint,
  layout: GridLayout,
  transform: CanvasTransform,
): readonly CellPosition[] {
  const span = Math.hypot(to.x - from.x, to.y - from.y);
  const steps = Math.max(1, Math.ceil((span * 2) / Math.max(transform.cellSize, 1)));

  const cells: CellPosition[] = [];
  let last: CellPosition | null = null;

  for (let step = 0; step <= steps; step += 1) {
    const ratio = step / steps;
    const cell = hitTest(
      from.x + (to.x - from.x) * ratio,
      from.y + (to.y - from.y) * ratio,
      layout,
      transform,
    );

    if (!cell) continue;
    // Comparar solo con la anterior no basta: un segmento puede volver sobre
    // una celda ya visitada más atrás.
    if (last?.row === cell.row && last.column === cell.column) continue;
    if (cells.some((current) => current.row === cell.row && current.column === cell.column)) {
      continue;
    }

    cells.push(cell);
    last = cell;
  }

  return cells;
}

/** Hay filas del diseño por encima del borde superior visible. */
export function hasRowsAbove(transform: CanvasTransform): boolean {
  return transform.offsetY < -0.5;
}

/** Hay filas del diseño por debajo del borde inferior visible. */
export function hasRowsBelow(
  layout: GridLayout,
  transform: CanvasTransform,
  viewport: Viewport,
): boolean {
  const contentHeight = layout.rowCount * transform.cellSize;
  return contentHeight + transform.offsetY > viewport.height + 0.5;
}
